<i18n lang="json">
{
  "en": {
    "appDownloadBanner": {
      "text": "For the best experience get the RVezy Mobile App.",
      "googlePlayAction": "Get it on Google Play",
      "iTunesAction": "Get it on the App Store"
    }
  },
  "fr": {
    "appDownloadBanner": {
      "text": "Téléchargez notre application mobile pour la meilleure expérience.",
      "googlePlayAction": "Disponible sur Google Play",
      "iTunesAction": "Télécharger dans l'App Store"
    }
  }
}
</i18n>
<template>
  <div class="flex gap-2">
    <template v-if="isMobileOrTablet">
      <a data-testid="store-banner-link" href="#" @click.prevent="() => downloadApp">
        <img
          data-testid="store-banner-img"
          :alt="downloadButtonImage.alt"
          :class="imgSizeCss"
          class="block w-auto md:h-12 xl:h-14"
          :src="downloadButtonImage.src"
          loading="lazy"
          :width="downloadButtonImage.width"
          :height="downloadButtonImage.height"
        />
      </a>
    </template>
    <template v-else>
      <a class="block cursor-pointer" data-testid="itunes-store-link" @click.prevent="downloadApp({ platform: 'ios' })">
        <img
          :alt="iTunesButtonImage.alt"
          :class="imgSizeCss"
          class="block w-auto md:h-12 xl:h-14"
          :src="iTunesButtonImage.src"
          loading="lazy"
          :width="iTunesButtonImage.width"
          :height="iTunesButtonImage.height"
        />
      </a>
      <a
        class="block cursor-pointer"
        data-testid="google-play-link"
        @click.prevent="downloadApp({ platform: 'android' })"
      >
        <img
          :alt="playStoreButtonImage.alt"
          class="block w-auto md:h-12 xl:h-14"
          :class="imgSizeCss"
          :src="playStoreButtonImage.src"
          loading="lazy"
          :width="playStoreButtonImage.width"
          :height="playStoreButtonImage.height"
        />
      </a>
    </template>
  </div>
</template>

<script setup lang="ts">
const { t, locale } = useI18n()

const { isAndroid, isMobileOrTablet } = useDevice()
const runtimeConfig = useRuntimeConfig()

const props = withDefaults(
  defineProps<{
    btnSize?: string
  }>(),
  {
    btnSize: 'small'
  }
)

const imgSize = computed(() => {
  switch (props.btnSize) {
    case 'large':
      return {
        width: '189',
        height: '56'
      }
    case 'medium':
      return {
        width: '162',
        height: '48'
      }
    default:
      return {
        width: '108',
        height: '32'
      }
  }
})

const imgSizeCss = computed(() => {
  switch (props.btnSize) {
    case 'large':
      return 'w-[11.8125rem] h-14'
    case 'medium':
      return 'w-[10.125rem] h-12'
    default:
      return 'w-[6.75rem] h-8'
  }
})

const downloadButtonImage = computed(() => {
  return isAndroid ? playStoreButtonImage.value : iTunesButtonImage.value
})

const playStoreButtonImage = computed(() => {
  return {
    src: `${runtimeConfig.public.app.staticCdnEndpoint}/app/download/badge-play-store_${locale.value}.svg`,
    alt: t('appDownloadBanner.googlePlayAction'),
    ...imgSize.value
  }
})

const iTunesButtonImage = computed(() => {
  return {
    src: `${runtimeConfig.public.app.staticCdnEndpoint}/app/download/badge-app-store_${locale.value}.svg`,
    alt: t('appDownloadBanner.iTunesAction'),
    ...imgSize.value
  }
})

const downloadApp = async ({ platform }: { platform?: string }) => {
  if (platform === 'android') {
    window.open(runtimeConfig.public.app.playStoreUrl)
    return
  }

  if (platform === 'ios') {
    window.open(runtimeConfig.public.app.appStoreUrl)
    return
  }
}
</script>
